// ═══════════════════════════════════════════════════════════════
// PublicDeletionPage.jsx — Standalone page for unauthenticated
// account deletion. Two phases driven by URL params:
//
//   ?action=delete-account    → email-request form
//                                Calls requestAccountDeletion;
//                                shows a generic "if registered..."
//                                response to avoid enumeration.
//
//   ?action=confirm-delete&token=... → confirmation flow
//                                Validates token via
//                                confirmAccountDeletion. Uses the
//                                same multi-step confirm UX as the
//                                in-app modal.
//
// Renders OUTSIDE the main app providers — no auth required.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from 'react';
import { AlertTriangle, Loader2, CheckCircle, Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { STRIPE_CONFIG, OWL_LOGO } from '../shared.jsx';

const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

async function callCloudFunction(endpoint, body) {
  const response = await fetch(`${STRIPE_CONFIG.cloudFunctionUrl}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return { status: response.status, body: await response.json().catch(() => ({})) };
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <img src={OWL_LOGO} alt="" className="w-12 h-12 rounded-xl mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-stone-800">The ArguMentor</h1>
        </div>
        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-6">
          {children}
        </div>
        <p className="text-center text-xs text-stone-500 mt-4">
          Need help? Contact <a href="mailto:info@4hci.com" className="text-amber-700 hover:underline">info@4hci.com</a>
        </p>
      </div>
    </div>
  );
}

// ── Phase 1: email request form ─────────────────────────────────
function RequestForm() {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState('input'); // input | submitting | submitted
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setPhase('submitting');
    setErrorMsg('');
    try {
      const { status, body } = await callCloudFunction('requestAccountDeletion', { email: email.trim() });
      if (status === 200) {
        setPhase('submitted');
      } else {
        setErrorMsg(body?.error || `Server returned ${status}. Please try again.`);
        setPhase('input');
      }
    } catch (e) {
      setErrorMsg(e.message || 'Network error. Please check your connection.');
      setPhase('input');
    }
  };

  if (phase === 'submitted') {
    return (
      <div className="space-y-4 text-sm text-stone-700">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <Mail className="w-5 h-5 text-green-700" />
          </div>
          <h2 className="text-lg font-semibold text-stone-800">Check your email</h2>
        </div>
        <p>If <span className="font-medium">{email}</span> is associated with an ArguMentor account, you&apos;ll receive a deletion confirmation link within a few minutes.</p>
        <p className="text-xs text-stone-600">The link will expire in 15 minutes. Click it to complete the deletion.</p>
        <p className="text-xs text-stone-500">If you don&apos;t receive the email, check your spam folder or contact info@4hci.com.</p>
        <a href="/" className="inline-block text-xs text-amber-700 hover:underline mt-2">← Back to The ArguMentor</a>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm text-stone-700">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <h2 className="text-lg font-semibold text-stone-800">Request Account Deletion</h2>
      </div>

      <p>Enter the email address associated with your ArguMentor account. We&apos;ll send you a confirmation link to complete the deletion.</p>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        <p className="font-medium mb-1">Before you continue, please note:</p>
        <ul className="space-y-1 list-disc pl-5">
          <li>Your login, profile, and private data will be permanently deleted.</li>
          <li>Conflict content you participated in is retained for 7 years in pseudonymized form (see Privacy Policy &sect; 6.5).</li>
          <li>Active subscriptions are cancelled (monthly at period end; annual with prorated refund).</li>
        </ul>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={phase === 'submitting'}
          className="w-full px-4 py-3 border-2 border-stone-200 rounded-lg focus:outline-none focus:border-red-500 text-sm"
        />
        {errorMsg && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{errorMsg}</p>
        )}
        <button
          type="submit"
          disabled={phase === 'submitting'}
          className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-stone-300 text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
        >
          {phase === 'submitting' ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          ) : (
            <>Send deletion link <ArrowRight className="w-4 h-4" /></>
          )}
        </button>
      </form>

      <a href="/" className="block text-xs text-amber-700 hover:underline mt-2 inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to The ArguMentor
      </a>
    </div>
  );
}

// ── Phase 2: token-link confirmation ────────────────────────────
function ConfirmForm({ token }) {
  const [step, setStep] = useState('disclosure'); // disclosure | typeConfirm | progress | success | error
  const [typedPhrase, setTypedPhrase] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const phraseMatches = typedPhrase === CONFIRM_PHRASE;

  const handleDelete = useCallback(async () => {
    setStep('progress');
    setErrorMessage('');
    try {
      const { status, body } = await callCloudFunction('confirmAccountDeletion', {
        token,
        confirmPhrase: CONFIRM_PHRASE,
      });
      if (status === 200 && body?.success) {
        setStep('success');
      } else {
        setErrorMessage(body?.error || `Server returned ${status}. The link may have expired. Please request a new one.`);
        setStep('error');
      }
    } catch (e) {
      setErrorMessage(e.message || 'Network error. Please check your connection and try again.');
      setStep('error');
    }
  }, [token]);

  return (
    <div className="space-y-4 text-sm text-stone-700">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          {step === 'success' ? <CheckCircle className="w-5 h-5 text-green-700" /> : <AlertTriangle className="w-5 h-5 text-red-600" />}
        </div>
        <h2 className="text-lg font-semibold text-stone-800">
          {step === 'success' ? 'Account Deleted' : 'Confirm Account Deletion'}
        </h2>
      </div>

      {step === 'disclosure' && (
        <>
          <p className="font-medium text-stone-800">This action is permanent and cannot be undone.</p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
            <p className="font-medium text-red-900 mb-2">Permanently deleted:</p>
            <ul className="text-red-800 space-y-1 list-disc pl-5">
              <li>Your login credentials</li>
              <li>Your name, email, and avatar from active records</li>
              <li>Your mood entries and push notification tokens</li>
              <li>Your free-tier tool history (SIYW, Mood Journal, Vote Mode)</li>
            </ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
            <p className="font-medium text-amber-900 mb-2">Retained for 7 years (legal hold):</p>
            <ul className="text-amber-800 space-y-1 list-disc pl-5">
              <li>Conflict content you participated in (your identity becomes &quot;Deleted User&quot;)</li>
              <li>A sealed legal-hold record consultable only via valid legal process</li>
            </ul>
            <p className="text-amber-700 mt-2">Privacy Policy &sect; 6.5 has the full details.</p>
          </div>
          <button
            onClick={() => setStep('typeConfirm')}
            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        </>
      )}

      {step === 'typeConfirm' && (
        <>
          <p>To confirm, type <span className="font-mono font-bold text-red-700">{CONFIRM_PHRASE}</span> in the box below exactly as shown.</p>
          <input
            type="text"
            value={typedPhrase}
            onChange={(e) => setTypedPhrase(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            autoFocus
            className="w-full px-4 py-3 border-2 border-stone-200 rounded-lg focus:outline-none focus:border-red-500 font-mono text-sm"
          />
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => { setTypedPhrase(''); setStep('disclosure'); }}
              className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleDelete}
              disabled={!phraseMatches}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
            >
              Delete My Account
            </button>
          </div>
        </>
      )}

      {step === 'progress' && (
        <div className="py-8 flex flex-col items-center gap-3 text-stone-600">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
          <p className="text-sm">Deleting your account…</p>
          <p className="text-xs text-stone-500">Please don&apos;t close this window.</p>
        </div>
      )}

      {step === 'success' && (
        <>
          <p>Your account has been deleted successfully.</p>
          <p className="text-xs text-stone-600">A confirmation has been sent to your email. Conflict content you participated in remains pseudonymized for 7 years per Privacy Policy &sect; 6.5.</p>
          <p className="text-xs text-stone-600">Thank you for being part of The ArguMentor.</p>
          <a href="/" className="block mt-2 py-3 bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-lg text-sm font-medium text-center transition-colors">
            Done
          </a>
        </>
      )}

      {step === 'error' && (
        <>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="font-medium text-red-900 mb-1">Account deletion failed</p>
            <p className="text-xs text-red-800">{errorMessage}</p>
          </div>
          <div className="flex items-center justify-end gap-3">
            <a href="/?action=delete-account" className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors">
              Request New Link
            </a>
            <button
              onClick={() => { setStep('typeConfirm'); setErrorMessage(''); }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Entry point: dispatches based on URL params ────────────────
function PublicDeletionPage() {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const action = params.get('action');
  const token = params.get('token');

  if (action === 'confirm-delete' && token) {
    return (
      <PageShell>
        <ConfirmForm token={token} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <RequestForm />
    </PageShell>
  );
}

export default PublicDeletionPage;
